import {
  actorSchema,
  federationStartSchema,
  loginResponseSchema,
  mfaStatusSchema,
  type Actor,
  type FederationStart,
  type LoginResponse,
} from "@kavoosh/contracts";

const API_BASE = import.meta.env.VITE_API_BASE ?? "/api/v1";

const INVALID_CREDENTIALS = "Invalid email or password.";
const GENERIC_FAILURE = "Sign in could not be completed. Please try again.";
const INVALID_MFA = "The authenticator or recovery code is invalid.";
const RECOVERY_REQUEST_FAILURE = "Password recovery could not be started. Please try again.";
const RECOVERY_CONFIRMATION_FAILURE = "This password recovery link is invalid or expired.";

export class AdminLoginError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AdminLoginError";
  }
}

export class PasswordRecoveryError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PasswordRecoveryError";
  }
}

export type LocalAdminLoginResult = LoginResponse & { mfa_required: boolean };
export type FederationAvailability = {
  google: boolean;
  organization_oidc: boolean;
};

function cookie(name: string): string | undefined {
  return document.cookie
    .split(";")
    .map((item) => item.trim())
    .find((item) => item.startsWith(`${name}=`))
    ?.slice(name.length + 1);
}

function isLocalPlatformAdmin(actor: Actor): boolean {
  return (
    actor.workspace === "platform_admin" &&
    actor.workspace_home === "/admin" &&
    actor.organization_id === null
  );
}

async function revokeUnexpectedSession(): Promise<void> {
  const csrf = cookie("kavoosh_csrf");
  await fetch(`${API_BASE}/auth/logout`, {
    method: "POST",
    credentials: "include",
    headers: csrf ? { "X-CSRF-Token": decodeURIComponent(csrf) } : {},
  }).catch(() => undefined);
}

async function publicError(response: Response): Promise<string> {
  if (response.status === 401) return INVALID_CREDENTIALS;
  if (response.status === 429) {
    return "Too many sign-in attempts. Please wait and try again.";
  }
  return GENERIC_FAILURE;
}

async function verifySession(expectedUserId: string): Promise<Actor> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/auth/me`, {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
  } catch {
    await revokeUnexpectedSession();
    throw new AdminLoginError(GENERIC_FAILURE);
  }

  if (!response.ok) {
    await revokeUnexpectedSession();
    throw new AdminLoginError(GENERIC_FAILURE);
  }

  const parsed = actorSchema.safeParse(await response.json().catch(() => null));
  if (!parsed.success || parsed.data.user_id !== expectedUserId) {
    await revokeUnexpectedSession();
    throw new AdminLoginError(GENERIC_FAILURE);
  }
  return parsed.data;
}

async function pendingLoginMfaRequired(): Promise<boolean> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/auth/mfa/status`, {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
  } catch {
    await revokeUnexpectedSession();
    throw new AdminLoginError(GENERIC_FAILURE);
  }
  if (!response.ok) {
    await revokeUnexpectedSession();
    throw new AdminLoginError(GENERIC_FAILURE);
  }
  const parsed = mfaStatusSchema.safeParse(await response.json().catch(() => null));
  if (!parsed.success) {
    await revokeUnexpectedSession();
    throw new AdminLoginError(GENERIC_FAILURE);
  }
  return parsed.data.totp_configured || parsed.data.recovery_codes_remaining > 0;
}

export async function getCurrentAdmin(): Promise<Actor> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/auth/me`, {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
  } catch {
    throw new AdminLoginError(GENERIC_FAILURE);
  }
  if (!response.ok) throw new AdminLoginError(GENERIC_FAILURE);
  const parsed = actorSchema.safeParse(await response.json().catch(() => null));
  if (!parsed.success || !isLocalPlatformAdmin(parsed.data)) {
    await revokeUnexpectedSession();
    throw new AdminLoginError(GENERIC_FAILURE);
  }
  return parsed.data;
}

export async function getFederationAvailability(
  email: string,
  options: { signal?: AbortSignal } = {},
): Promise<FederationAvailability> {
  const normalized = email.trim();
  if (!normalized.includes("@")) return { google: false, organization_oidc: false };
  try {
    const response = await fetch(`${API_BASE}/auth/federation/availability`, {
      method: "POST",
      cache: "no-store",
      ...(options.signal ? { signal: options.signal } : {}),
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ email: normalized }),
    });
    if (!response.ok) return { google: false, organization_oidc: false };
    const value = await response.json().catch(() => null) as Partial<FederationAvailability> | null;
    return {
      google: value?.google === true,
      organization_oidc: value?.organization_oidc === true,
    };
  } catch {
    return { google: false, organization_oidc: false };
  }
}

export async function beginGoogleSignIn(email: string): Promise<FederationStart> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/auth/federation/login`, {
      method: "POST",
      cache: "no-store",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ email: email.trim(), kind: "google" }),
    });
  } catch {
    throw new AdminLoginError(GENERIC_FAILURE);
  }
  if (response.status === 429) {
    throw new AdminLoginError("Too many sign-in attempts. Please wait and try again.");
  }
  if (!response.ok) throw new AdminLoginError("Google sign-in is unavailable for this account.");
  const parsed = federationStartSchema.safeParse(await response.json().catch(() => null));
  if (!parsed.success) throw new AdminLoginError(GENERIC_FAILURE);
  return parsed.data;
}

export async function changeRequiredPassword(input: {
  currentPassword: string;
  newPassword: string;
}): Promise<void> {
  const csrf = cookie("kavoosh_csrf");
  const response = await fetch(`${API_BASE}/auth/password/change`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(csrf ? { "X-CSRF-Token": decodeURIComponent(csrf) } : {}),
    },
    body: JSON.stringify({
      current_password: input.currentPassword,
      new_password: input.newPassword,
    }),
  });
  if (!response.ok) throw new AdminLoginError(await publicError(response));
}

export async function logoutAdmin(): Promise<void> {
  const csrf = cookie("kavoosh_csrf");
  const response = await fetch(`${API_BASE}/auth/logout`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(csrf ? { "X-CSRF-Token": decodeURIComponent(csrf) } : {}),
    },
  });
  if (!response.ok) throw new AdminLoginError(GENERIC_FAILURE);
}

export async function requestPasswordRecovery(email: string): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/auth/password-recovery`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ email: email.trim() }),
    });
  } catch {
    throw new PasswordRecoveryError(RECOVERY_REQUEST_FAILURE);
  }
  if (response.status === 429) {
    throw new PasswordRecoveryError("Too many recovery attempts. Please wait and try again.");
  }
  if (!response.ok) throw new PasswordRecoveryError(RECOVERY_REQUEST_FAILURE);
}

export async function completePasswordRecovery(input: {
  token: string;
  newPassword: string;
}): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/auth/password-recovery/confirmation`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ token: input.token, new_password: input.newPassword }),
    });
  } catch {
    throw new PasswordRecoveryError(RECOVERY_CONFIRMATION_FAILURE);
  }
  if (response.status === 429) {
    throw new PasswordRecoveryError("Too many recovery attempts. Please wait and try again.");
  }
  if (!response.ok) throw new PasswordRecoveryError(RECOVERY_CONFIRMATION_FAILURE);
}

export async function loginAsLocalAdmin(input: {
  identifier: string;
  password: string;
}): Promise<LocalAdminLoginResult> {
  const identifier = input.identifier.trim();
  if (!identifier || !identifier.includes("@")) throw new AdminLoginError(INVALID_CREDENTIALS);

  let response: Response;
  try {
    response = await fetch(`${API_BASE}/auth/login`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ email: identifier, password: input.password }),
    });
  } catch {
    throw new AdminLoginError(GENERIC_FAILURE);
  }

  if (!response.ok) throw new AdminLoginError(await publicError(response));

  const parsed = loginResponseSchema.safeParse(await response.json().catch(() => null));
  if (!parsed.success) {
    await revokeUnexpectedSession();
    throw new AdminLoginError(GENERIC_FAILURE);
  }

  if (parsed.data.actor.setup_required) {
    return { ...parsed.data, mfa_required: false };
  }

  const mfaRequired = await pendingLoginMfaRequired();
  if (mfaRequired) {
    return { ...parsed.data, mfa_required: true };
  }
  const actor = await verifySession(parsed.data.actor.user_id);
  return { ...parsed.data, actor, mfa_required: false };
}

export async function completeLocalAdminMfa(input: {
  code: string;
  expectedUserId: string;
}): Promise<Actor> {
  const csrf = cookie("kavoosh_csrf");
  if (!csrf) {
    await revokeUnexpectedSession();
    throw new AdminLoginError(GENERIC_FAILURE);
  }
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/auth/step-up`, {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": decodeURIComponent(csrf),
      },
      body: JSON.stringify({ code: input.code }),
    });
  } catch {
    throw new AdminLoginError(GENERIC_FAILURE);
  }
  if (response.status === 429) {
    throw new AdminLoginError("Too many verification attempts. Please wait and try again.");
  }
  if (!response.ok) {
    if (response.status === 401) throw new AdminLoginError(INVALID_MFA);
    throw new AdminLoginError(GENERIC_FAILURE);
  }
  const elevated = actorSchema.safeParse(await response.json().catch(() => null));
  if (
    !elevated.success ||
    elevated.data.user_id !== input.expectedUserId ||
    !["mfa", "phishing_resistant"].includes(elevated.data.assurance_level)
  ) {
    await revokeUnexpectedSession();
    throw new AdminLoginError(GENERIC_FAILURE);
  }
  return verifySession(input.expectedUserId);
}

export async function cancelLocalAdminLogin(): Promise<void> {
  await revokeUnexpectedSession();
}
